/**
 * Shared Calendar primitive — Plan 0002 §7.
 *
 * One controlled `<Calendar>` with Day / Week / Month views, consumed
 * by both the Appointments and Tasks surfaces. Appointments and tasks
 * both map onto the single `CalendarEvent` shape, so the component
 * never knows which domain it is rendering.
 */

export type CalendarView = "day" | "week" | "month";

export type CalendarEventKind = "appointment" | "task";

/**
 * Visual tone — maps to the design-system tokens, nothing else. The
 * consumer derives it from domain status (e.g. an appointment's
 * `confirmed` → "success", `no_show` → "muted").
 */
export type CalendarEventTone =
  | "default"
  | "success"
  | "warning"
  | "danger"
  | "muted";

/**
 * Maps lead status to a calendar color token. Used by appointment-list
 * and calendar views to colour-code by pipeline stage.
 */
export const LEAD_STATUS_COLOR_KEY: Record<string, CalendarEventTone> = {
  new: "default",
  working: "warning",
  appointment: "success",
  won: "success",
  lost: "muted",
};

/**
 * Maps contact lifecycle status to the same semantic tone vocabulary used by
 * leads and appointments. Contact directories and record views should consume
 * this map instead of defining page-local color classes.
 */
export const CONTACT_STATUS_COLOR_KEY: Record<string, CalendarEventTone> = {
  anonymous: "muted",
  new_lead: "default",
  engaged: "default",
  appointment_scheduled: "default",
  appointment_attended: "success",
  sold: "success",
  service_customer: "success",
  inactive: "muted",
  lost: "muted",
};

/**
 * Maps appointment status to a calendar color token.
 * This is the SINGLE source of appointment-status→color truth across the
 * calendar event, list table, mobile list, and agenda board.
 * Unknown statuses fall back to "muted" (same as StatusPill).
 * WHY a separate map from LEAD_STATUS_COLOR_KEY: appointment status (10 values)
 * and lead status (5 values) are different key vocabularies on the same token set.
 */
export const APPOINTMENT_STATUS_COLOR_KEY: Record<string, CalendarEventTone> = {
  unconfirmed:      "warning",
  pending_customer: "warning",
  confirmed:        "default",
  checked_in:       "default",
  in_service:       "default",
  ready_for_pickup: "success",
  completed:        "success",
  cancelled:        "muted",
  no_show:          "danger",
  rescheduled:      "warning",
};

export interface CalendarEvent {
  id: string;
  /** Start instant. Required. */
  start: Date;
  /** End instant. Omitted for point-in-time events (e.g. a task's due time). */
  end?: Date;
  title: string;
  /** Secondary line — contact name, vehicle, type. */
  subtitle?: string;
  kind: CalendarEventKind;
  /** Defaults to "default". */
  tone?: CalendarEventTone;
  /** Lead status at render time — drives colour via {@link LEAD_STATUS_COLOR_KEY}. */
  status?: string;
  /** Channel the appointment was booked on (sms, email, web, …). */
  channel?: string;
  /** Short vehicle label from the linked VOI, e.g. "2024 Honda Civic". */
  vehicleLabel?: string;
  /** Pre-resolved colour token override — skips the status→color derivation. */
  colorKey?: CalendarEventTone;
}

export interface CalendarProps {
  events: CalendarEvent[];
  /** Active view — controlled. */
  view: CalendarView;
  /** The date the view is centred on — controlled. */
  focusDate: Date;
  onViewChange: (view: CalendarView) => void;
  onFocusDateChange: (date: Date) => void;
  /** Fired when an event is activated (click / Enter). */
  onEventClick?: (event: CalendarEvent) => void;
  /** Fired when an empty time slot (day/week) or day cell (month) is clicked. */
  onSlotClick?: (slotStart: Date) => void;
  /** Shown in place of the grid when true. */
  isLoading?: boolean;
  className?: string;
}
